// ═══════════════════════════════════════════════════════════════════════════════
// PatientPicker — compact search inline patient chooser
// ═══════════════════════════════════════════════════════════════════════════════
//
// PURPOSE
//   Reusable inline patient search for the briefing-preview drawers. Hosts
//   pass a `searchPatients(query) => Promise<MemberSearchHit[]>` callback
//   so the picker works against any backend (Tenant API, AGUI API, or a
//   future direct DB shim).
//
// DESIGN NOTES (from the design pass)
//   - Session-first default: the drawer pre-fills the patient and only shows
//     this picker when (a) no patient is in session, or (b) the user clicks
//     "Change patient". The picker is therefore a SECONDARY surface — small,
//     non-intrusive, easy to dismiss.
//   - Search debounces 250 ms; minimum 2 chars to fire.
//   - Returns the raw MemberSearchHit so the host can carry the language
//     code into its own session if needed.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import { Input, Spin, Empty } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { MemberSearchHit } from './types';

export interface PatientPickerProps {
  /** Async search — the host wires this to its API. */
  searchPatients: (query: string) => Promise<MemberSearchHit[]>;
  /** Called when the user picks a result. */
  onPick: (hit: MemberSearchHit) => void;
  /** Optional — id of the currently-selected patient (shown as ✓ current). */
  currentMemberId?: number;
  /** Optional placeholder override. */
  placeholder?: string;
  /** Optional initial query (used when the host opens the picker mid-flow). */
  initialQuery?: string;
}

const DEBOUNCE_MS = 250;
const MIN_CHARS   = 2;

export function PatientPicker({
  searchPatients,
  onPick,
  currentMemberId,
  placeholder = 'Search by name or MRN…',
  initialQuery = '',
}: PatientPickerProps) {
  const [query, setQuery]       = useState(initialQuery);
  const [hits, setHits]         = useState<MemberSearchHit[] | null>(null);
  const [loading, setLoading]   = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Each new keystroke cancels the prior in-flight call. The ref holds the
  // token of the last request we're willing to accept results for.
  const requestToken = useRef(0);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_CHARS) {
      setHits(null);
      setLoading(false);
      setErrorMsg(null);
      return;
    }

    const myToken = ++requestToken.current;
    const timer   = setTimeout(async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        const result = await searchPatients(trimmed);
        if (myToken !== requestToken.current) return; // stale
        setHits(result);
      } catch (e: unknown) {
        if (myToken !== requestToken.current) return;
        setErrorMsg(e instanceof Error ? e.message : 'Search failed.');
        setHits([]);
      } finally {
        if (myToken === requestToken.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, searchPatients]);

  return (
    <div style={{ padding: 12, background: '#FAFBFD', border: '1px solid #E5E7EB', borderRadius: 10 }}>
      <Input
        autoFocus
        size="middle"
        prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        allowClear
      />

      {/* Hint when the user hasn't typed enough yet */}
      {query.trim().length > 0 && query.trim().length < MIN_CHARS && (
        <div style={{ marginTop: 8, fontSize: 11.5, color: '#94A3B8' }}>
          Type at least {MIN_CHARS} characters…
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div style={{ marginTop: 12, textAlign: 'center', padding: 8 }}>
          <Spin size="small" />
        </div>
      )}

      {/* Error state */}
      {errorMsg && !loading && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#7F1D1D', background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 6, padding: '6px 10px' }}>
          {errorMsg}
        </div>
      )}

      {/* Results */}
      {hits && !loading && (
        hits.length === 0 ? (
          <div style={{ marginTop: 14 }}>
            <Empty description="No patients found." imageStyle={{ height: 30 }} />
          </div>
        ) : (
          <div style={{ marginTop: 8, maxHeight: 280, overflowY: 'auto' }}>
            {hits.map((h) => (
              <HitRow
                key={h.id}
                hit={h}
                isCurrent={currentMemberId === h.id}
                onPick={() => onPick(h)}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}

function HitRow({
  hit, isCurrent, onPick,
}: {
  hit: MemberSearchHit;
  isCurrent: boolean;
  onPick: () => void;
}) {
  const name = [hit.firstName, hit.middleName, hit.lastName].filter(Boolean).join(' ') || `Member #${hit.id}`;
  const initials = buildInitials(hit);
  const meta = [
    formatAgeSex(hit.dob, hit.gender),
    hit.mrn ? `MRN ••••${tail4(hit.mrn)}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <button
      type="button"
      onClick={onPick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', padding: '8px 10px',
        background: 'transparent', border: 'none', borderBottom: '1px solid #EEF2F7',
        cursor: 'pointer', textAlign: 'left',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = '#F0F7FF')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <span style={{
        width: 26, height: 26, borderRadius: '50%',
        background: '#E0E7FF', color: '#3730A3',
        fontSize: 11, fontWeight: 600,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>{initials}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 500, fontSize: 13, color: '#0F172A' }}>{name}</span>
        {meta && <span style={{ display: 'block', fontSize: 11, color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace' }}>{meta}</span>}
      </span>
      {isCurrent ? (
        <span style={{ fontSize: 11, color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace' }}>✓ current</span>
      ) : (
        <span style={{ fontSize: 11, color: '#2563EB', fontWeight: 600 }}>Use →</span>
      )}
    </button>
  );
}

function buildInitials(h: MemberSearchHit): string {
  const fi = h.firstName?.trim()?.[0]?.toUpperCase() ?? '';
  const li = h.lastName?.trim()?.[0]?.toUpperCase() ?? '';
  return (fi + li) || '?';
}

function tail4(s: string): string {
  const t = s.trim();
  return t.length <= 4 ? t : t.slice(-4);
}

function formatAgeSex(dob?: string, gender?: string): string | null {
  const age = ageFromDob(dob);
  const sex = simplifySex(gender);
  if (age == null && !sex) return null;
  if (age == null) return sex || null;
  return `${age}${sex}`;
}

function ageFromDob(dob?: string): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age >= 0 ? age : null;
}

function simplifySex(g?: string): string {
  if (!g) return '';
  const t = g.trim().toLowerCase();
  if (t.startsWith('m')) return 'M';
  if (t.startsWith('f')) return 'F';
  return '';
}
